import { Moment } from 'moment';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { connect } from 'react-redux';
import { getApiConfig } from '../../config';
import { GoogleAnalytics } from '../../services/google-analytics';
import { BaseModal } from '../../ui';
import { toggleDieticianModal, toggleMealModal, toggleNutritionStepsModal } from '../../ui';
import { i18n, UserDataConsumer } from '../../utils';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { defaultScale } from '../../utils/scaling';
import { isMobile } from '../../utils/ScreenSizes';
import { setUserMealPlan, toggleMealSelectionWizardScreen } from '../actions';
import { MealDetailedView } from '../components';
import { MealSelection } from '../components/MealSelection';
import { showMealSelectionOverviewSlector } from '../selectors';

interface IProps {
  isMealModalVisible: boolean;
  toggleMealModal: (toggle: boolean) => void;
  mealOfDay: string;
  currentMeal: any;
  day: any;
  mealPlans: any; // TODO: proably a MealPlanWithMeals
  user: any;
  nutritionPreference: any;
  changeMeal: any;
  toggleNutritionStepsModal: (visible: boolean) => void;
  toggleNutritionVideosModal: (visible: boolean) => void;
  toggleMealSelectionWizardScreen: () => void;
  mealChanged: boolean;
  mealsForTimeOfDay: any;
  showMealSelectionOverView: boolean;
  t: (str: string, object?: {[key: string]: string}) => string;
}
const getTitle = (day: Moment, mealOfDay: string,
                  showMealSelectionOverView: boolean, currentMealDayOfWeak: string) => {
  const modalLaunchedFromDashboard = !!currentMealDayOfWeak;
  const dayString = modalLaunchedFromDashboard ? currentMealDayOfWeak : day.format('dddd');
  const title = `${dayString} ${i18n.t(`nutrition:${mealOfDay}`)}`;
  if (!showMealSelectionOverView) {
    return title;
  }
  return title.length > 22 ?  `${day.format('ddd')} ${i18n.t(`nutrition:${mealOfDay}`)}` : title;
};
const MealModalComponent: FunctionComponent<IProps> = ({
  isMealModalVisible,
  toggleMealModal: toggleModal,
  day,
  currentMeal,
  mealOfDay,
  mealPlans,
  user,
  changeMeal,
  toggleNutritionStepsModal: toggleStepsModal,
  toggleNutritionVideosModal,
  mealChanged,
  showMealSelectionOverView,
  toggleMealSelectionWizardScreen,
  mealsForTimeOfDay,
  t,
}) => {
  const mealSelectionOverViewTitle = getTitle(day, mealOfDay, showMealSelectionOverView, currentMeal.dayOfWeek);

  const selectDifferentMeal = (mealId) => {
    let newMeal = {};
    mealsForTimeOfDay[mealOfDay].forEach((meal) => {
      if (meal._id === mealId) {
        newMeal = meal;
      }
    });
    changeMeal(mealId, mealOfDay, day, newMeal);
  };

  const closeMealModalAndOpenDietitianModal = () => {
    toggleModal(false);
    toggleNutritionVideosModal(true);
  };

  const closeModal = () => {
    toggleModal(false);
  };

  if (!mealPlans) {
    return <></>;
  }

  return (
    <BaseModal
      headerTextStyle={styles.headerTextStyle}
      isModalVisible={isMealModalVisible}
      hideCloseButton={true}
      useSafeAreaView={!showMealSelectionOverView}
      showHeader={false}
      bodyStyle={styles.bodyStyle}
      closeModal={showMealSelectionOverView ? closeModal : toggleMealSelectionWizardScreen}
      containerStyle={isMobile && Platform.OS === 'web' ? {height : '100vh'} : null}
    >
      <View style={styles.modalContainer}>
        { showMealSelectionOverView && <MealDetailedView
          meal={currentMeal}
          shoppingListServingCount={mealPlans.shoppingListServingCount || 1}
          closeModal={closeModal}
          modalTitle={mealSelectionOverViewTitle}
          closeMealModalAndOpenDieticianModal={closeMealModalAndOpenDietitianModal}
          toggleMealSelectionWizardScreen={toggleMealSelectionWizardScreen}
        />}
        { !showMealSelectionOverView &&
          <MealSelection
            toggleMealSelectionWizardScreen={toggleMealSelectionWizardScreen}
            mealOfDay={mealOfDay}
            modalTitle={t('changeMealForDay', { mealOfDay : mealSelectionOverViewTitle})}
            nutritionService={user.nutritionService}
            mealPlanId={mealPlans._id}
            t={t}
            mealChanged={selectDifferentMeal}
          />
        }
      </View>
    </BaseModal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
  },
  headerTextStyle: {
    ...Platform.select({
      web: {
        borderBottomWidth: defaultScale(2),
        padding: 0,
      },
    }),
  },
  bodyStyle: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
});

const mapStateToProps = (state: any) => {
  return {
    isMealModalVisible: state.ui.mealModalVisible,
    mealOfDay: state.nutrition.mealOfDayOpenInModal,
    currentMeal: state.nutrition.mealOpenInModal,
    day: state.nutrition.viewingMealsForDay,
    mealPlans: state.nutrition.mealPlans,
    nutritionPreference: state.nutrition.nutritionPreference,
    mealChanged: state.nutrition.mealChanged,
    showMealSelectionOverView: showMealSelectionOverviewSlector(state),
    mealsForTimeOfDay: state.nutrition.mealsForTimeOfDay,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { user: { nutritionService } } = ownProps;

  return {
    toggleMealModal: (visible) => dispatch(toggleMealModal(visible)),
    changeMeal: (mealId, mealOfDay, day, newMeal) =>
      dispatch(setUserMealPlan(nutritionService, mealId, mealOfDay, day, newMeal)),
    toggleNutritionStepsModal: (visible) => dispatch(toggleNutritionStepsModal(visible)),
    toggleNutritionVideosModal: (visible) => dispatch(toggleDieticianModal(visible)),
    toggleMealSelectionWizardScreen: () => dispatch(toggleMealSelectionWizardScreen()),
  };
};

export const MealModal = UserDataConsumer(
  translate(['nutrition'], { wait: true })(connect(mapStateToProps, mapDispatchToProps)(MealModalComponent)),
);
